import type { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import type { GradeElementaryMiddle } from '@mathflat/domain/@entities/SchoolSystem/schema'

import type { Academy } from '@/dto'
import { RouteName } from '@/router'
import { curriculumCoreService } from '@/service/curriculumCore.service'

type ValidateSubscriptionParamType =
  | {
      schoolType: common.SchoolType | null
      grade?: common.GradeSubject | null
    }
  | { trieKey: CurriculumEntity.TrieKey | null }

export class FeaturePermission {
  private _academyFeatureAuthority: res.Academy['authority']
  private _subscribeItems: res.Academy['subscribeItems']
  private _isSeoltab: boolean

  constructor({
    academyFeatureAuthority,
    subscribeItems,
    payStatus: _payStatus,
    isSeoltab,
  }: {
    academyFeatureAuthority: res.Academy['authority']
    subscribeItems: res.Academy['subscribeItems']
    payStatus: Academy['payStatus']
    isSeoltab: boolean
  }) {
    this._academyFeatureAuthority = academyFeatureAuthority
    this._subscribeItems = subscribeItems
    this._isSeoltab = isSeoltab
  }

  get isSolo() {
    return this._academyFeatureAuthority === 'SOLO'
  }

  get 솔로_구독_커리큘럼() {
    if (!this.isSolo) return null
    if (this._subscribeItems?.length === 0) return null
    return (
      this._subscribeItems?.map((v) => ({
        ...v,
        grade: `${v.grade}` as GradeElementaryMiddle,
      })) ?? []
    )
  }
  validateSubscription(params: ValidateSubscriptionParamType) {
    if (!this.isSolo) return true

    if ('trieKey' in params) {
      if (!params.trieKey) return true
      return this._isSubscribedByTrieKey(params.trieKey)
    } else if (params.schoolType && params.grade) {
      const schoolType = params.schoolType
      const grade = params.grade
      switch (schoolType) {
        case 'ELEMENTARY':
        case 'MIDDLE':
        default:
          return this._isSubscribedByGrade(schoolType, grade)
        case 'HIGH': {
          if (Number.isInteger(grade)) {
            return this._isSubscribedByGrade(schoolType, grade)
          }
          const targetCurriculumKey = curriculumCoreService.getItemByGradeSemesterInfo({
            schoolType,
            gradeSubject: grade!,
            semester: null,
          })?.trieKey

          return targetCurriculumKey ? this._isSubscribedByTrieKey(targetCurriculumKey) : false
        }
      }
    } else if (params.schoolType) {
      return this._isSubscribedBySchoolType(params.schoolType)
    } else {
      return true
    }
  }

  private _isSubscribedBySchoolType(schoolType: common.SchoolType): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some((sub) => sub.schoolType === schoolType)
  }

  private _isSubscribedByGrade(
    schoolType: common.SchoolType,
    grade: common.GradeSubject | null,
  ): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some(
      (sub) => sub.schoolType === schoolType && sub.grade === grade,
    )
  }

  private _isSubscribedByTrieKey(trieKey: CurriculumEntity.TrieKey): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some((sub) => sub.trieKeys.includes(trieKey))
  }

  isThemeWorksheetSubscribed(routeName: ValueOf<typeof RouteName>): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }

    const info = THEME_WORKSHEET_SUBSCRIPTION_MAPPER[routeName]

    const checkIsSubscribed = (
      subscriptionList: CurriculumInfo[],
      userSubscription: {
        schoolType: common.SchoolType
        grade: common.GradeSubject | null
        trieKeys: string[]
      },
    ) =>
      subscriptionList.some(({ schoolType, grade, trieKeys }) => {
        if (trieKeys) {
          return userSubscription.trieKeys.every((key) => trieKeys.includes(key))
        }
        if (grade) {
          return userSubscription.schoolType === schoolType && userSubscription.grade === grade
        }
        return userSubscription.schoolType === schoolType
      })

    if (info.blackList.length !== 0) {
      // 구독하고 있는 모든 학제/학년 중 하나라도 블랙리스트 조건에 해당하지 않는다면 구독 상태
      return this.솔로_구독_커리큘럼.some(
        (subscribe) => !checkIsSubscribed(info.blackList, subscribe),
      )
    }

    if (info.whiteList.length !== 0) {
      // 구독하고 있는 모든 학제/학년 중 하나라도 화이트리스트 조건에 해당한다면 구독 상태
      return this.솔로_구독_커리큘럼.some((subscribe) =>
        checkIsSubscribed(info.whiteList, subscribe),
      )
    }

    return true
  }

  get firstSubscriptedSchoolType() {
    return this.솔로_구독_커리큘럼?.[0]?.schoolType ?? 'MIDDLE'
  }

  get firstSubscriptionGrade() {
    return this.firstSubscriptionBySchoolType[this.firstSubscriptedSchoolType]?.grade ?? undefined
  }

  get firstSubscriptionTrieKeys() {
    return this.firstSubscriptionBySchoolType[this.firstSubscriptedSchoolType]?.trieKeys ?? []
  }

  get everyTrieKeys() {
    return this.솔로_구독_커리큘럼?.map((sub) => sub.trieKeys).flat() ?? []
  }

  get everySemesterSubjectIds() {
    return this.everyTrieKeys
      .map((key) => key.split('.')[key.split('.').length - 1])
      .filter((str) => str !== undefined)
  }

  private get firstSubscriptionBySchoolType() {
    const checkFirstSubscriptionBySchoolType = (schoolType: common.SchoolType) => {
      return this.솔로_구독_커리큘럼?.find((sub) => sub.schoolType === schoolType)
    }

    return {
      HIGH: checkFirstSubscriptionBySchoolType('HIGH'),
      MIDDLE: checkFirstSubscriptionBySchoolType('MIDDLE'),
      ELEMENTARY: checkFirstSubscriptionBySchoolType('ELEMENTARY'),
    }
  }

  validateContentSubscription({
    contentSchoolType,
    contentGrade,
  }: {
    contentSchoolType: common.SchoolType
    contentGrade: common.GradeSubject
  }): boolean {
    if (!this.isSolo) return true
    if (!this.솔로_구독_커리큘럼) return false

    const item = curriculumCoreService.getItemByGradeSemesterInfo({
      schoolType: contentSchoolType,
      gradeSubject: contentGrade,
      semester: null,
    })
    return item?.subscribeStatus === 'SUBSCRIBED'
  }
}

type CurriculumInfo =
  | {
      schoolType: common.SchoolType
      grade?: common.GradeSubject | null
      semester?: common.Semester
      trieKeys?: never
    }
  | { trieKeys: CurriculumEntity.TrieKey[]; schoolType?: never; grade?: never; semester?: never }

export type SubscriptionCurriculum = {
  isUnusedDefaultDescription?: boolean
  labelType: ('GRADE_SUBJECT' | 'GRADE_ONLY')[]
  descriptions: string[]
  whiteList: CurriculumInfo[]
  blackList: CurriculumInfo[]
  excludeRevision22?: boolean
}

export const getMakeWorksheetSubscriptionMapper = (): Record<string, SubscriptionCurriculum> => ({
  [RouteName.editWorksheetProblems]: {
    labelType: [],
    isUnusedDefaultDescription: true,
    descriptions: [
      '수능/모의고사 학습지의 쌍둥이・유사 기능을 사용하려면',
      '요금제 업그레이드를 진행해주세요.',
    ],
    whiteList: [],
    blackList: [],
  },
})

export const THEME_WORKSHEET_SUBSCRIPTION_MAPPER: Record<string, SubscriptionCurriculum> = {}
